import _camelCase from 'lodash/camelCase';
import _startCase from 'lodash/startCase';
import _upperFirst from 'lodash/upperFirst';

import { TCustomerDetails } from 'containers/Customer/i18n/TModules';
import i18n from 'utils/i18n';

export const AUDIENCE_ENTITY = 'v4_audiences';
export const FILTER_PREFIX = 'filter_';
export const FILE_PREFIX = 'file_';
export const CUSTOM_ATTRIBUTES_KEY = 'filter_custom_attributes';
export const NAME_KEY = 'name';

const T_AUDIENCE = `${TCustomerDetails}.AuditLog.Audience`;

export type TAudienceFileAction = 'uploaded' | 'removed' | 'relationChanged';

export interface IAudienceFileChange {
  fileName: string;
  action: TAudienceFileAction;
  fromRelation: string;
  toRelation: string;
}

export interface IAudienceDetailLine {
  label: string;
  before: string[];
  after: string[];
}

const FILE_ACTION_PHRASE: Record<TAudienceFileAction, string> = {
  uploaded: 'FileUploaded',
  removed: 'FileRemoved',
  relationChanged: 'FileRelationChanged'
};

const FILE_ACTION_ORDER: TAudienceFileAction[] = ['uploaded', 'removed', 'relationChanged'];

const translate = (suffix: string): string => i18n.t(`${T_AUDIENCE}.${suffix}`);

const asObject = (value: any): any =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : null;

// A change key is only present when it changed, but a side may still be null
// (uploaded has no before, removed has no after).
export const classifyFileChange = (data: any[]): IAudienceFileChange | null => {
  const [oldVal, newVal] = data || [];
  const before = asObject(oldVal);
  const after = asObject(newVal);
  if (!before && !after) return null;

  let action: TAudienceFileAction = 'relationChanged';
  if (!before) action = 'uploaded';
  else if (!after) action = 'removed';

  return {
    fileName: (after || before).name || '',
    action,
    fromRelation: before ? before.relation || '' : '',
    toRelation: after ? after.relation || '' : ''
  };
};

export const fileActionText = (change: IAudienceFileChange): string => {
  if (change.action === 'uploaded') {
    return `${translate('Uploaded')} ${change.toRelation}`.trim();
  }
  if (change.action === 'removed') {
    return translate('Removed');
  }
  return `${translate('RelationChangedTo')} ${change.toRelation}`.trim();
};

export const audienceNameLabel = (): string => translate('NameLabel');

export const criterionLabel = (propertyName: string): string => {
  const suffix = propertyName.slice(FILTER_PREFIX.length);
  const key = `${T_AUDIENCE}.Criteria.${_upperFirst(_camelCase(suffix))}`;
  return i18n.exists(key) ? i18n.t(key) : _startCase(suffix);
};

// Renders a stored criterion as readable `Label: value` lines. Anything left as an
// object would otherwise reach Cell's Fallback and print JSON, which the FR forbids.
export const formatCriterionValue = (value: any): string[] => {
  if (value === null || value === undefined || value === '') return [];
  if (Array.isArray(value)) {
    return value.reduce((acc: string[], item) => acc.concat(formatCriterionValue(item)), []);
  }
  if (typeof value === 'object') {
    return Object.keys(value).reduce((acc: string[], key) => {
      const inner = value[key];
      if (inner === null || inner === undefined || inner === '') return acc;
      if (typeof inner === 'object') return acc.concat(formatCriterionValue(inner));
      return acc.concat(`${_startCase(key)}: ${inner}`);
    }, []);
  }
  return [String(value)];
};

// `filter_custom_attributes` arrives pre-pruned by the backend as { groups: [...] },
// each entry carrying the same (before_index, after_index) pair on both sides.
export const decomposeCustomAttributes = (data: any[]): IAudienceDetailLine[] => {
  const [beforeSide, afterSide] = data || [];
  const beforeGroups = (asObject(beforeSide) || {}).groups || [];
  const afterGroups = (asObject(afterSide) || {}).groups || [];

  const paired: Record<string, { before?: any; after?: any }> = {};
  const order: string[] = [];
  const put = (group: any, side: 'before' | 'after') => {
    const key = `${group.before_index}:${group.after_index}`;
    if (!paired[key]) {
      paired[key] = {};
      order.push(key);
    }
    paired[key][side] = group;
  };
  beforeGroups.forEach((group: any) => put(group, 'before'));
  afterGroups.forEach((group: any) => put(group, 'after'));

  return order.reduce((lines: IAudienceDetailLine[], key) => {
    const { before, after } = paired[key];

    // A group the backend declined to diff (it carries an `exclude` criterion).
    if ((before && before.raw) || (after && after.raw)) {
      return lines.concat({
        label: translate('AttributeGroupRaw'),
        before: formatCriterionValue(before && before.raw),
        after: formatCriterionValue(after && after.raw)
      });
    }

    const names = Object.keys({
      ...((before && before.attributes) || {}),
      ...((after && after.attributes) || {})
    });

    return lines.concat(
      names.map(name => ({
        label: `${translate('AttributeGroup')} — ${_startCase(name)}`,
        before: formatCriterionValue(before && before.attributes && before.attributes[name]),
        after: formatCriterionValue(after && after.attributes && after.attributes[name])
      }))
    );
  }, []);
};

export const isAudienceEntity = (entity?: string): boolean => entity === AUDIENCE_ENTITY;

// 4001 is the generic GeneralError code, but the only GeneralError `changes2` raises is
// "Unknown record type." — an API that predates PLA-9521. 40361 is an unknown audience.
// Either way there is no history to show, so render nothing rather than a retry panel.
const UNAVAILABLE_CODES = [4001, 40361];

export const isAudienceLogUnavailable = (entity: string | undefined, error: any): boolean =>
  isAudienceEntity(entity) && UNAVAILABLE_CODES.includes(error?.response?.data?.code);

// One phrase per KIND of change, never repeated, ordered name -> filters -> files.
export const buildAudienceSummary = (record: any): string => {
  if (record.event === 'create') return translate('Created');

  const changes = record.changes || {};
  const keys = Object.keys(changes).filter(key => (changes[key] || []).some(Boolean));
  const phrases: string[] = [];
  const push = (suffix: string) => {
    const phrase = translate(suffix);
    if (!phrases.includes(phrase)) phrases.push(phrase);
  };

  if (keys.includes(NAME_KEY)) push('NameUpdated');
  if (keys.some(key => key.startsWith(FILTER_PREFIX))) push('FiltersUpdated');

  const actions = keys
    .filter(key => key.startsWith(FILE_PREFIX))
    .map(key => classifyFileChange(changes[key]))
    .filter(Boolean)
    .map((change: IAudienceFileChange) => change.action);

  FILE_ACTION_ORDER.forEach(action => {
    if (actions.includes(action)) push(FILE_ACTION_PHRASE[action]);
  });

  return phrases.join(', ');
};
